import { useQuery } from '@tanstack/react-query'

import { useSession, useSyncState } from '@/contexts/SessionContext'
import { rows } from '@/db/database'
import { HttpResponse } from '@/clients/http'

import { unwrap } from '../unwrap'

/** This branch's terminals from the device database (pulled for the branch only). */
export const useBranchTerminals = () => {
  const { device } = useSession()
  const sync = useSyncState()
  return useQuery({
    queryKey: ['terminals', 'branch', device?.branch_id, sync.lastSyncedAt],
    queryFn: async () =>
      (await rows('terminals').toArray())
        .filter((t) => !t.deleted_at && t.branch_id === device?.branch_id)
        .sort((a, b) => Number(a.unique_no) - Number(b.unique_no)),
    enabled: !!device,
  })
}

/**
 * Online screens need the server and a signed-in session; they say so instead of failing (§15.8 U1 is about the
 * till: selling never depends on it).
 */
export const useOnline = () => {
  const sync = useSyncState()
  return sync.status !== 'offline' && sync.status !== 'auth'
}

/** An online read: retried when the connection comes back, never cached across a branch change. */
export const useOfficeQuery = <T>(key: unknown[], request: () => Promise<HttpResponse<T>>, enabled = true) => {
  const online = useOnline()
  const { device } = useSession()
  return useQuery({ queryKey: ['office', device?.branch_id, ...key], queryFn: () => unwrap(request()), enabled: enabled && online && !!device })
}
